import { PROGRAMS_BUDGET_MAP } from 'components/common/vendorConfig';
import type { Observation } from 'fhir/r4';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { CSSProperties } from 'react';
import { Card, Divider, Icon, Label, List, ListItem, Loader, Popup } from 'semantic-ui-react';
import type { SemanticCOLORS } from 'semantic-ui-react';
import type { ProgramBudget } from 'types';
import { getProgramBudget, mapKey } from 'utils';
import { calculateProgramWeekNumber, getUtcToday } from 'utils/enrollmentCounterUtils';
import { resolveMaternityCounterDates } from 'utils/maternityCounterUtils';

import * as Helpers from '../../../utils/Helpers';

/**
 * The three optional fields come from `ObservationHelper` getters that walk
 * optional FHIR fields. Both consumers tolerate absence: `moment.utc(undefined)`
 * yields an invalid moment rather than throwing, and the "Previous holds" list
 * renders `undefined` as nothing.
 */
interface OnHoldPeriod {
  id: string | undefined;
  startDate: string | undefined;
  endDate: string | undefined;
  onHoldWeeks: number;
  programWeekAtHold?: number;
}

interface ProfileCounterProps {
  data?: unknown;
  org?: string;
  /**
   * `false` arrives here: the banner forwards `Patient360`'s `referralId` state,
   * which is seeded `false` and holds it on every render before the profile query
   * resolves. Both reads are truthiness-guarded, so `false` and `''` behave alike.
   * Unlike `interventioncStartDate` below, whose `false` arm is unreachable.
   */
  referralId?: string | false;
  /**
   * The shared `ProgramBudget`, not a stricter local restatement of it.
   *
   * This prop used to declare a file-local `ProgramBudgetEntry` with
   * `programFoodBudget`, `referralId`, `id` and `programOrderCost` all required.
   * No producer builds that: every one of them threads the shared
   * `ProgramBudget`, whose fields are all optional, and the only thing this
   * component does with the value is hand it to `getProgramBudget`, which also
   * takes the shared type. The stricter local type was a claim nothing checked,
   * and it hid the optionality that the `programFoodBudget` read below relies on.
   */
  programBudget?: ProgramBudget[];
  /**
   * The program-week anchor date, or `''` when there is no active enrollment
   * task. The `| false` arm this used to carry is unreachable: `patient/banner`
   * is the only producer and forwards `Patient360`'s `provenanceCreateDate`
   * state, which is seeded and reset with `''` and otherwise holds a date
   * string. Both guards below treat `''` and `false` identically.
   */
  interventioncStartDate?: string;
  lastInterventionLaunchDate?: string;
  /*
   * `| undefined` as well as `?:` on the three below: the banner spreads values that
   * are themselves optional, and `exactOptionalPropertyTypes` will not pass the
   * `undefined` arm through a bare `?:`. Every read here already tolerates it.
   */
  observation?: unknown[] | undefined;
  profileData?: unknown;
  maternityObservations?: unknown;
  patientId?: string | undefined;
  sidebar?: boolean | undefined;
  style?: CSSProperties;
}

export default function ProfileCounter(props: ProfileCounterProps) {
  const [gestationLoading, setGestationLoading] = useState<boolean>(false);
  const [programLoading, setProgramLoading] = useState<boolean>(true);
  const [budgetLoading, setBudgetLoading] = useState<boolean>(true);
  const [programWeeks, setProgramWeeks] = useState<string>();
  const [gestationWeeks, setGestationWeeks] = useState<string>();
  const [onHoldGroups, setOnHoldGroups] = useState<OnHoldPeriod[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [onHoldStatus, setOnHoldStatus] = useState(false);
  // Both writes below can be absent — `ProgramBudget.programFoodBudget` is optional
  // and the `PROGRAMS_BUDGET_MAP` read is an open-key `?.BUDGET`. The budget card
  // renders straight into JSX, so an absent budget shows blank where the seed shows
  // "0".
  const [programMAXBudget, setProgramMAXBudget] = useState<number | undefined>(0);

  let programWeekNumber: string;

  // The maternity counter is a pure function of the patient's observations, so deleting a
  // delivery date and re-deriving postpartum state is consistent with a full page refresh.
  const {
    maternityDate: resolvedMaternityDate,
    isPostpartum,
    postpartumReferenceDate,
  } = useMemo(
    () => resolveMaternityCounterDates(undefined, undefined, [props.profileData, props.maternityObservations]),
    [props.profileData, props.maternityObservations]
  );

  const hasExpectedDueDate = resolvedMaternityDate && moment(resolvedMaternityDate, 'YYYY-MM-DD', true).isValid();
  const hasMaternityDate = hasExpectedDueDate || isPostpartum;

  // The read below is asserted rather than guarded: both callers of `counterCard`
  // pass a key this map carries, and `Label`'s `color?:` rejects the `undefined` arm
  // `noUncheckedIndexedAccess` adds.
  const labelColors: Record<string, SemanticCOLORS> = {
    intervention: 'blue',
    gestation: isPostpartum ? 'purple' : 'green',
  };

  useEffect(() => {
    const hasDueDate = resolvedMaternityDate && moment(resolvedMaternityDate, 'YYYY-MM-DD', true).isValid();

    if (!hasDueDate && !isPostpartum) {
      setGestationWeeks(undefined);
      setGestationLoading(false);
      return;
    }

    setGestationLoading(true);

    const referenceDate = isPostpartum ? postpartumReferenceDate : resolvedMaternityDate;

    if (!referenceDate || !moment(referenceDate, 'YYYY-MM-DD', true).isValid()) {
      setGestationWeeks(undefined);
      setGestationLoading(false);
      return;
    }

    const referenceMoment = moment.utc(referenceDate, 'YYYY-MM-DD');
    let weekNumber: string;

    if (isPostpartum) {
      weekNumber = (moment.utc().diff(referenceMoment, 'week') + 1).toString();
    } else {
      const weeksUntilDue = referenceMoment.diff(moment.utc(), 'week');
      weekNumber = (39 - weeksUntilDue).toString();
    }

    setGestationWeeks(weekNumber);
    setGestationLoading(false);
  }, [resolvedMaternityDate, isPostpartum, postpartumReferenceDate]);

  const getOnHoldWeek = useCallback(
    (onHoldData: OnHoldPeriod[]): OnHoldPeriod[] | null => {
      if (!props?.interventioncStartDate) {
        return null;
      }

      const programStartDate = moment.utc(props.interventioncStartDate, 'YYYY-MM-DD', true).isValid()
        ? moment.utc(props.interventioncStartDate, 'YYYY-MM-DD').format('YYYY-MM-DD')
        : moment.utc(props.interventioncStartDate).format('YYYY-MM-DD');
      const today = getUtcToday();

      const sortedHolds = [...onHoldData].sort((a, b) => {
        return moment.utc(a.startDate).valueOf() - moment.utc(b.startDate).valueOf();
      });

      let totalOnHoldDays = 0;

      sortedHolds.forEach((period, index) => {
        const holdStart = moment.utc(period.startDate).format('YYYY-MM-DD');
        const holdEnd = moment.utc(period.endDate).format('YYYY-MM-DD');

        const effectiveStart = holdStart;
        const effectiveEnd = holdEnd > today ? today : holdEnd;

        let holdDays = 0;
        if (moment.utc(effectiveEnd).isAfter(moment.utc(effectiveStart))) {
          holdDays = moment.utc(effectiveEnd).diff(moment.utc(effectiveStart), 'days') + 1;
        } else if (moment.utc(effectiveEnd).isSame(moment.utc(effectiveStart))) {
          holdDays += 1;
        }
        let activeDaysBeforeHold = moment.utc(effectiveStart).diff(moment.utc(programStartDate), 'days');

        for (let i = 0; i < index; i++) {
          const prevStart = moment.utc(sortedHolds[i]!.startDate);
          const prevEnd = moment.utc(sortedHolds[i]!.endDate);

          const clampedPrevEnd = moment.min(prevEnd, moment.utc(effectiveStart));
          if (clampedPrevEnd.isAfter(prevStart)) {
            const overlapDays = clampedPrevEnd.diff(prevStart, 'days') + 1;
            activeDaysBeforeHold -= overlapDays;
          }
        }

        const week = Math.max(1, Math.floor(Math.max(0, activeDaysBeforeHold) / 7) + 1);
        totalOnHoldDays += holdDays;
        period.programWeekAtHold = week;
      });
      setProgramWeeks(calculateProgramWeekNumber(programStartDate, totalOnHoldDays).toString());
      return sortedHolds;
    },
    [props.interventioncStartDate]
  );

  useEffect(() => {
    setProgramLoading(true);
    setOnHoldStatus(false);

    if (props.interventioncStartDate) {
      // programStartDate is week-1 day-1 (may be backdated from enrollment-start-week).
      // Shared day-based formula never produces negative weeks.
      programWeekNumber = calculateProgramWeekNumber(String(props.interventioncStartDate)).toString();
    } else {
      programWeekNumber = 'NA';
    }

    const onHoldObservation: OnHoldPeriod[] = [];
    let onHoldObservationExists = false;
    const observation = props?.observation;
    if (observation && observation.length > 0) {
      for (let i = 0; i < observation.length; i++) {
        const currentObs = observation[i] as Observation;
        const observationHelper = new Helpers.ObservationHelper(currentObs);
        const id = observationHelper.getId();
        const obsCode = observationHelper.getCode();
        const obsCategory = observationHelper.getCategoryCode();
        if (obsCode === 'QUALIFICATION-STATUS' && obsCategory === 'member-program-on-hold-pw') {
          onHoldObservationExists = true;
          const startDate = observationHelper.getStartDate();
          const endDate = observationHelper.getDueDate();
          const onHoldWeeks = moment.utc(endDate).diff(moment.utc(startDate), 'weeks');
          const today = getUtcToday();
          if (
            ((moment.utc(endDate).isAfter(moment.utc(today)) && moment.utc(startDate).isBefore(moment.utc(today))) ||
              moment.utc(endDate).isSame(moment.utc(today)) ||
              moment.utc(startDate).isSame(moment.utc(today))) &&
            moment.utc(props?.lastInterventionLaunchDate).isSameOrBefore(moment.utc(startDate))
          ) {
            setOnHoldStatus(true);
          }
          if (moment.utc(props?.lastInterventionLaunchDate).isSameOrBefore(moment.utc(startDate))) {
            const obj = {
              id,
              startDate,
              endDate,
              onHoldWeeks,
            };
            onHoldObservation.push(obj);
          }
        }
      }
      if (!onHoldObservationExists) {
        setProgramWeeks(programWeekNumber);
      } else {
        const holds = getOnHoldWeek(onHoldObservation);
        if (holds) {
          setOnHoldGroups(holds);
        } else {
          setProgramWeeks(programWeekNumber);
        }
      }
    } else {
      setProgramWeeks(programWeekNumber);
    }

    setProgramLoading(false);
  }, [props.interventioncStartDate, props.lastInterventionLaunchDate, props?.observation, getOnHoldWeek]);

  useEffect(() => {
    setBudgetLoading(true);
    if (props.referralId && props.programBudget && props.programBudget.length > 0) {
      const budget = getProgramBudget(props.referralId, props.programBudget);
      setProgramMAXBudget(budget.programFoodBudget);
    } else {
      setProgramMAXBudget(PROGRAMS_BUDGET_MAP[mapKey(props.org?.toUpperCase())]?.BUDGET);
    }
    setBudgetLoading(false);
  }, [props.programBudget, props.referralId, props.org]);

  // `weekNum` is absent before the counters resolve, and the gestation effect writes
  // `undefined` back when there is no due date. Rendered straight into JSX, where
  // `undefined` renders as nothing.
  const counterCard = (
    description: string,
    weekNum: string | undefined,
    counterType: string,
    isLoading: boolean
  ) => {
    return (
      <>
        <Card
          fluid
          style={{ width: '100px' }}
          className={onHoldStatus ? 'ui red horizontal label' : 'basic blue label'}
        >
          <Card.Header textAlign="center" style={{ color: onHoldStatus ? 'white' : 'black' }}>
            <div style={{ padding: 1, fontSize: '12px' }}>
              <b>{description}</b>
            </div>
          </Card.Header>
          <Card.Description textAlign="center">
            {counterType !== 'intervention' && (
              <Label circular size="huge" color={labelColors[counterType]!} style={{ margin: '5px', minwidth: '25px' }}>
                {isLoading && <Loader active inline="centered" />}
                {!isLoading && <b>{weekNum}</b>}
              </Label>
            )}
            {counterType === 'intervention' && (
              <Label
                circular
                size="huge"
                className={onHoldStatus ? 'ui white label' : 'ui blue label'}
                style={{ margin: '5px', minwidth: '25px', color: onHoldStatus ? 'red' : '' }}
              >
                {isLoading && <Loader active inline="centered" />}
                {!isLoading && <b>{weekNum}</b>}
              </Label>
            )}
            {counterType === 'intervention' && onHoldStatus && !isLoading && <p style={{ color: 'white' }}>ON HOLD</p>}
          </Card.Description>
        </Card>
      </>
    );
  };

  const programBudgetCard = (isLoading: boolean) => {
    return (
      <>
        <Card fluid style={{ width: '100px' }} className={'basic blue label'}>
          <Card.Header textAlign="center" style={{ color: 'black' }}>
            <div style={{ padding: 1, fontSize: '12px' }}>
              <b>Program Budget</b>
            </div>
          </Card.Header>
          <Card.Description textAlign="center">
            <Label circular size="huge" color={'orange'} style={{ margin: '5px', minwidth: '25px' }}>
              {isLoading && <Loader active inline="centered" />}
              {!isLoading && <b>{programMAXBudget}</b>}
            </Label>
          </Card.Description>
        </Card>
      </>
    );
  };

  return (
    <>
      <Divider style={{ margin: '5px' }} />
      <List horizontal size="medium" style={{ display: 'flex' }}>
        <List.Item style={{ display: 'flex' }}>
          {counterCard('Program weeks:', programWeeks, 'intervention', programLoading)}
        </List.Item>
        {PROGRAMS_BUDGET_MAP[mapKey(props.org?.toUpperCase())]?.ACTIVE && (
          <List.Item style={{ display: 'flex' }}>{programBudgetCard(budgetLoading)}</List.Item>
        )}
        {hasMaternityDate && (
          <List.Item style={{ display: 'flex' }}>
            {isPostpartum
              ? counterCard('Post-partum:', gestationWeeks, 'gestation', gestationLoading)
              : counterCard('Gestation weeks:', gestationWeeks, 'gestation', gestationLoading)}
          </List.Item>
        )}
      </List>
      {onHoldGroups?.length > 0 && (
        <List>
          Previous holds ({onHoldGroups?.length}){' '}
          <Popup
            id={'on-hold-observation'}
            wide="very"
            on="click"
            position="top right"
            closeOnEscape
            hideOnScroll
            closeOnDocumentClick
            onClose={() => {
              setIsOpen(false);
            }}
            trigger={
              <Icon
                name="warning"
                link
                color="blue"
                style={{ marginLeft: 5, fontSize: '1.2rem' }}
                onClick={() => {
                  setIsOpen(true);
                }}
              />
            }
            open={isOpen}
            content={
              <>
                <List bulleted>
                  {onHoldGroups.map((obs) => {
                    return (
                      <ListItem key={obs.id}>
                        Hold at week {obs.programWeekAtHold} from {obs.startDate} to {obs.endDate} ({obs.onHoldWeeks}{' '}
                        weeks)
                      </ListItem>
                    );
                  })}
                </List>
              </>
            }
          />
        </List>
      )}
    </>
  );
}
